import { createApi } from '@reduxjs/toolkit/query/react';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';
import {
  AdminRecipientListItem,
  IAdminRecipientDetail,
  LookupRecipient,
  PatchRecipientPayload,
} from 'store/recipients/types';

export const adminRecipientsApi = createApi({
  reducerPath: 'adminRecipientsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AdminRecipients'],
  endpoints: builder => ({
    getAdminRecipients: builder.query<AdminRecipientListItem[], void>({
      query: () => API_ROUTES.GET_ADMIN_RECIPIENTS,
      transformResponse: (
        response: { recipients: AdminRecipientListItem[] } | AdminRecipientListItem[]
      ) => {
        return Array.isArray(response) ? response : response.recipients;
      },
      providesTags: ['AdminRecipients'],
    }),

    updateRecipientStripeAuthorized: builder.mutation<
      void,
      { recipientId: string; isStripeAuthorized: boolean }
    >({
      query: ({ recipientId, isStripeAuthorized }) => ({
        url: API_ROUTES.PATCH_RECIPIENT_STRIPE_AUTHORIZED(recipientId),
        method: 'PUT',
        body: { stripe_authorized: isStripeAuthorized },
      }),
      invalidatesTags: ['AdminRecipients'],
    }),

    lookupRecipients: builder.query<
      LookupRecipient[],
      { displayName: string; excludeRecipientId?: string; entityType?: string }
    >({
      query: ({ displayName, excludeRecipientId, entityType }) =>
        API_ROUTES.LOOKUP_RECIPIENTS(displayName, excludeRecipientId, entityType),
      transformResponse: (response: { matches: LookupRecipient[] }) => response.matches,
    }),

    updateRecipientOnboardingComplete: builder.mutation<
      void,
      { recipientId: string; onboardingFormComplete: boolean }
    >({
      query: ({ recipientId, onboardingFormComplete }) => ({
        url: API_ROUTES.ADMINS_ONBOARDING_FORM_COMPLETE(recipientId),
        method: 'PUT',
        body: { onboarding_form_complete: onboardingFormComplete },
      }),
      invalidatesTags: ['AdminRecipients'],
    }),

    getAdminRecipient: builder.query<IAdminRecipientDetail, { recipientId: string }>({
      query: ({ recipientId }) => API_ROUTES.GET_ADMIN_RECIPIENT(recipientId),
      providesTags: (result, error, { recipientId }) => [
        { type: 'AdminRecipients' as const, id: recipientId },
      ],
    }),

    patchAdminRecipient: builder.mutation<
      IAdminRecipientDetail,
      {
        recipientId: string;
        body: PatchRecipientPayload;
        confirmDuplicate?: boolean;
        clearChariotKeys?: boolean;
        reregisterChariot?: boolean;
      }
    >({
      query: ({ recipientId, body, confirmDuplicate, clearChariotKeys, reregisterChariot }) => ({
        url: API_ROUTES.PATCH_ADMIN_RECIPIENT(recipientId, {
          confirmDuplicate,
          clearChariotKeys,
          reregisterChariot,
        }),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        'AdminRecipients',
        { type: 'AdminRecipients' as const, id: recipientId },
      ],
    }),
  }),
});

export const {
  useGetAdminRecipientsQuery,
  useLazyLookupRecipientsQuery,
  useUpdateRecipientStripeAuthorizedMutation,
  useUpdateRecipientOnboardingCompleteMutation,
  useGetAdminRecipientQuery,
  usePatchAdminRecipientMutation,
} = adminRecipientsApi;
